import { Getter } from 'vuex-class'
import { Vue, Component, Ref, Model, Prop } from 'vue-property-decorator'
import { RootGetters } from '@/store/types'
import { userModule } from '@/store/namespaces'
import { UserGetters } from '@/store/modules/user/types'
import uniqCid from '@/helpers/uniqCid'
import isEmpty from '@/helpers/isEmpty'
import { setControlBarDataInUrl } from '../../lib/url-search-data'
import { setControlBarDataInStorage } from '../../lib/user-storage'
import type {
  SimpleFilterField as ISimpleFilterField,
  DatepickerFilterField as IDatepickerFilterField,
  SelectionFilterField as ISelectionFilterField,
  SimpleFilterFieldInstance as ISimpleFilterFieldInstance,
} from '../../types'
import { FiltersFieldSearchMode as EFiltersFieldSearchMode } from '../../types'
import SimpleFilterField from './ui/SimpleFilterField/SimpleFilterField.vue'
import DatepickerFilterField from './ui/DatepickerFilterField/DatepickerFilterField.vue'
import Menu from '@/components/Menu/Menu.vue'
import { ProjectID } from '@/types/platform'

@Component({
  components: {
    SimpleFilterField,
    DatepickerFilterField,
    Menu,
  },
})
export default class FiltersField extends Vue {
  @userModule.Getter
  private readonly getUserId!: UserGetters['getUserId']

  @Getter('getPlatform') private readonly platform!: RootGetters['getPlatform']

  @Model('change', { type: Array })
  private readonly value!: ISelectionFilterField[]

  @Ref('menu') private readonly menuRef?: {
    vMenuRef?: {
      updateDimensions: () => void
      $refs?: {
        content?: HTMLDivElement
      }
    }
  }

  @Ref('searchInput') private readonly searchInputRef?: HTMLInputElement

  @Ref('simpleFilterField')
  private readonly simpleFilterFieldRef?: ISimpleFilterFieldInstance

  @Prop({ type: Array, required: true }) private readonly items!: Array<
    ISimpleFilterField | IDatepickerFilterField
  >

  @Prop({ type: String })
  private readonly essenceStorage?: string

  @Prop({ type: Boolean, default: false }) private readonly saveInUrl!: boolean
  @Prop({ type: String, default: '' })
  private readonly searchInputValue!: string

  private handleCloseMenuClassName = `js-handle-close-menu-${uniqCid()}`
  private localSearchInputValue = ''
  private selectedFieldName: string | null = null
  private isFocusedSearchInput = false

  private menu = {
    isShowed: false,
    maxWidth: 0,
  }

  private get isOutlined() {
    return this.isFocusedSearchInput || this.menu.isShowed
  }

  private get computedMenuMaxHeight() {
    return this.selectedField?.type === 'datepicker' ? 430 : 300
  }

  private get computedMenuMaxWidth() {
    return this.selectedField?.type === 'datepicker' ? 320 : this.menu.maxWidth
  }

  private get searchMode(): EFiltersFieldSearchMode {
    if (this.selectedField) {
      return EFiltersFieldSearchMode.SELECT_FIELD_VALUE
    }

    if (!this.localSelections.length && !isEmpty(this.localSearchInputValue)) {
      return EFiltersFieldSearchMode.TEXT
    }

    return EFiltersFieldSearchMode.SELECT_FIELD
  }

  private get isTextSearchMode() {
    return this.searchMode === EFiltersFieldSearchMode.TEXT
  }

  private get isSelectFieldSearchMode() {
    return this.searchMode === EFiltersFieldSearchMode.SELECT_FIELD
  }

  private get isSelectFieldValueSearchMode() {
    return this.searchMode === EFiltersFieldSearchMode.SELECT_FIELD_VALUE
  }

  private get isClearable() {
    return (
      !isEmpty(this.localSearchInputValue) || !isEmpty(this.localSelections)
    )
  }

  private get isReadonlySearchInput() {
    return (
      this.selectedField?.type === 'datepicker' ||
      (this.isSelectFieldSearchMode && !this.localFields.length)
    )
  }

  private get isDisabledSearchInput() {
    return this.isSelectFieldSearchMode && !this.localFields.length
  }

  private get menuContentClass() {
    const isHiddenMenu =
      this.isSelectFieldValueSearchMode &&
      this.selectedField?.type !== 'datepicker' &&
      !this.selectedField?.fetchItems &&
      !this.selectedField?.items?.length

    return [
      {
        value:
          'white rounded-0 filter-field-content-menu overscroll-behavior-contain',
      },
      {
        value: this.handleCloseMenuClassName,
      },
      {
        value: 'visibility-hidden',
        include:
          isHiddenMenu || this.isTextSearchMode || this.isDisabledSearchInput,
      },
    ]
      .reduce<string>((acc, { value, include = true }) => {
        if (include) {
          acc += ` ${value}`
        }

        return acc
      }, '')
      .trim()
  }

  private get fieldLabels() {
    return new Map(this.items.map(({ name, label }) => [name, label]))
  }

  private get localSelections() {
    const fieldName = this.selectedField?.name
    const fieldLabel = this.selectedField?.label
    const fieldLabels = this.fieldLabels

    const selections = this.value.reduce<
      Array<ISelectionFilterField & { label: string }>
    >((acc, item) => {
      const label = fieldLabels.get(item.field)

      if (!label || item.field === fieldName) {
        return acc
      }

      acc.push({ ...item, label })

      return acc
    }, [])

    if (fieldName && fieldLabel) {
      selections.push({
        field: fieldName,
        label: fieldLabel,
        text: '',
        value: null,
      })
    }

    return selections
  }

  private get localFields() {
    const localSelections = this.localSelections

    return this.items.filter(
      (item) => !localSelections.some(({ field }) => field === item.name)
    )
  }

  private get selectedField() {
    const selectedFieldName = this.selectedFieldName

    return this.items.find(({ name }) => name === selectedFieldName)
  }

  private onFocusSearchInput() {
    this.isFocusedSearchInput = true
  }

  private onBlurSearchInput() {
    this.isFocusedSearchInput = false
  }

  private onClickSearchInput() {
    this.toggleMenu(true)
  }

  private onChangeSearchInput() {
    this.emitChangeSearchInputValue(
      this.isTextSearchMode ? this.localSearchInputValue : ''
    )
  }

  private onEnterSearchInput() {
    this.submitFilters()
  }

  private focusSearchInput() {
    this.$nextTick(() => {
      this.searchInputRef?.focus()
      this.menuRef?.vMenuRef?.updateDimensions()
    })
  }

  private scrollToTopMenu() {
    this.menuRef?.vMenuRef?.$refs?.content?.scrollTo(0, 0)
  }

  private toggleMenu(value: boolean) {
    this.menu.isShowed = value
  }

  private initMenu() {
    let searchInputResizeObserver: ResizeObserver | null = null

    if (this.searchInputRef) {
      searchInputResizeObserver = new ResizeObserver(([entry]) => {
        this.menu.maxWidth = entry.target.clientWidth
        this.menuRef?.vMenuRef?.updateDimensions()
      })

      searchInputResizeObserver.observe(this.searchInputRef)
    }

    const onBodyClick = (event: Event) => {
      const target = event.target as Element | null

      if (target?.closest(`.${this.handleCloseMenuClassName}`)) {
        return
      }

      this.toggleMenu(false)

      if (
        this.selectedFieldName &&
        this.value?.some(({ field }) => field === this.selectedFieldName)
      ) {
        this.selectedFieldName = null
        this.localSearchInputValue = ''
      }
    }

    document.body.addEventListener('mousedown', onBodyClick)
    document.body.addEventListener('touchstart', onBodyClick)

    this.$once('hook:beforeDestroy', () => {
      searchInputResizeObserver?.disconnect()
      document.body.removeEventListener('mousedown', onBodyClick)
      document.body.removeEventListener('touchstart', onBodyClick)
    })
  }

  private clearValue() {
    const newValue: ISelectionFilterField[] = []

    this.$emit('change:value', newValue)
    this.saveFiltersInUrl(newValue)
    this.saveFiltersInStorage(newValue)
    this.emitChangeSearchInputValue('')

    if (this.value.length || this.isTextSearchMode) {
      this.$emit('submit', this.value)
    }

    this.selectedFieldName = null
    this.localSearchInputValue = ''

    if (this.menu.isShowed) {
      this.focusSearchInput()
    }
  }

  private unselectField(fieldName: string) {
    this.toggleMenu(false)

    const hasSelectedFieldName = !!this.selectedFieldName

    if (this.selectedFieldName === fieldName) {
      this.selectedFieldName = null
    }

    this.localSearchInputValue = ''
    this.scrollToTopMenu()

    const filterFieldIndex = this.value.findIndex(
      (item) => item.field === fieldName
    )

    const filterField =
      filterFieldIndex > -1 ? this.value[filterFieldIndex] : undefined

    if (filterField) {
      const newValue = this.value.concat()

      newValue.splice(filterFieldIndex, 1)

      this.saveFiltersInUrl(newValue)
      this.saveFiltersInStorage(newValue)
      this.$emit('change:value', newValue)
      this.submitFilters()
    }

    if (hasSelectedFieldName) {
      this.focusSearchInput()

      this.$nextTick(() => {
        this.toggleMenu(true)
      })
    }
  }

  private selectField(name: string) {
    this.toggleMenu(false)
    this.selectedFieldName = name

    this.focusSearchInput()
    this.scrollToTopMenu()

    this.$nextTick(() => {
      this.toggleMenu(true)
    })
  }

  private selectFieldBySelection(item: ISelectionFilterField) {
    if (item.field === this.selectedFieldName) {
      this.focusSearchInput()

      return
    }

    this.localSearchInputValue = item.text
    this.selectField(item.field)
  }

  private setItemValueForFilterField({
    field,
  }: Pick<ISelectionFilterField, 'field' | 'value'>) {
    const text = this.items.find((item) => item.name === field)?.label

    if (isEmpty(text)) {
      return text
    }
  }

  private setItemForFilterField(item: ISelectionFilterField) {
    if (!this.items.some(({ name }) => name === item.field)) {
      return
    }

    const newValue = this.value.concat([]) || []

    const selectionIndex = newValue.findIndex(
      ({ field }) => item.field === field
    )

    if (selectionIndex === -1) {
      newValue.push(item)
    } else {
      newValue.splice(selectionIndex, 1, item)
    }

    this.selectedFieldName = null
    this.localSearchInputValue = ''
    this.scrollToTopMenu()
    this.toggleMenu(false)
    this.saveFiltersInUrl(newValue)
    this.saveFiltersInStorage(newValue)
    this.$emit('change:value', newValue)

    this.submitFilters()
  }

  private emitChangeSearchInputValue(value: string) {
    this.$emit('change:search-input-value', value)

    if (this.saveInUrl) {
      setControlBarDataInUrl({
        searchInputValue: value,
      })
    }

    if (this.essenceStorage) {
      setControlBarDataInStorage({
        searchInputValue: value,
        essence: this.essenceStorage,
        platform: this.platform,
        userId: this.getUserId(ProjectID[this.platform]),
      })
    }
  }

  private submitFilters() {
    if (
      this.selectedField?.type !== 'datepicker' &&
      this.selectedField?.enterItem
    ) {
      this.selectedField.enterItem({
        items: this.simpleFilterFieldRef?.items ?? [],
        searchText: this.localSearchInputValue,
        filterFied: {
          name: this.selectedField.name,
          label: this.selectedField.label,
        },
        setItemForFilterField: this.setItemForFilterField,
      })
    } else if (this.selectedField) {
      this.simpleFilterFieldRef?.autoSelectFilterFieldItem()
    }

    this.$emit('submit', this.value)
  }

  private saveFiltersInStorage(value: ISelectionFilterField[]) {
    if (!this.essenceStorage) {
      return
    }

    setControlBarDataInStorage({
      essence: this.essenceStorage,
      filtersFieldValue: value,
      platform: this.platform,
      userId: this.getUserId(ProjectID[this.platform]),
    })
  }

  private saveFiltersInUrl(value: ISelectionFilterField[]) {
    if (!this.saveInUrl) {
      return
    }

    setControlBarDataInUrl({
      filterFields: value,
    })
  }

  private created() {
    this.$watch(
      () => {
        return this.searchInputValue
      },
      (searchInputValue) => {
        this.localSearchInputValue = searchInputValue
      },
      {
        immediate: true,
      }
    )
  }

  private mounted() {
    this.initMenu()
  }
}
